/** What a task asks for; drives the Inbox filters and the Workload columns. */
export type TaskKind = 'report' | 'assessment' | 'session' | 'checkin' | 'success' | 'renewal';

/** Overdue: a deadline has passed. Soon: due within a week or ready to act on. Upcoming: within two weeks. */
export type Urgency = 'overdue' | 'soon' | 'upcoming';

/** Programmes with alert rules and a cleared-alerts collection (`${programme}_alerts_cleared`). */
export type Programme = 'ignition' | 'foundation';
/** Every programme the dashboard recognises; Pre/Post Natal has no alert rules. */
export type ProgrammeFamily = Programme | 'natal';

export const isAlertProgramme = (programme: ProgrammeFamily | null): programme is Programme =>
  programme === 'ignition' || programme === 'foundation';

/** The independent reads a client's tasks are built from; each can load or fail on its own. */
export type SourceKey = 'checkIns' | 'success' | 'cleared' | 'reports' | 'submissions';
export type SourceStatus = 'loading' | 'ready' | 'error';
export type SourceStatuses = Record<SourceKey, SourceStatus>;

export interface Appointment {
  coachId: number | null;
  coachName: string;
  location: string;
  /** Wall-clock date and time as booked (the stored "Z" is not a real UTC offset). */
  date: string;
  time: { hours: number; minutes: number } | null;
}

/** A client document from the BigQuery mirror, flattened to what the dashboard shows. */
export interface DashboardClient {
  /** Firestore document id under comprehensive_details. */
  docId: string;
  /** Business client id; used for links, check-ins, success records and cleared alerts. */
  clientId: string;
  name: string;
  coachId: string | null;
  coachName: string;
  location: string;
  /** From the current programme's name; null for clients on any other package. */
  programme: ProgrammeFamily | null;
  programmeLabel: string;
  /** 1-based programme week, when the programme start date is known. */
  week: number | null;
  ptUsed: number | null;
  ptTotal: number | null;
  endsAt: Date | null;
  appointments: Appointment[];
  activeProgramme: boolean;
}

export interface ClientTask {
  /** Stable across reloads: `${clientId}:${key}`. */
  id: string;
  clientId: string;
  kind: TaskKind;
  programme: ProgrammeFamily | null;
  /** Verb first: "Generate the initial report". */
  title: string;
  /** Why it is due, in plain words. */
  detail: string;
  urgency: Urgency;
  /** When it fell or falls due; null when the rule has no date (an unsubmitted form). */
  dueAt: Date | null;
  /** The legacy alert string, kept verbatim so clearing writes what ignition/foundation_alerts_cleared already match on. */
  alertReason?: string;
  checkInType?: '2_week' | '10_week';
  /** Success updates only: no update has ever been written for this client. */
  awaitingFirst?: boolean;
}

export interface ClientWork {
  client: DashboardClient;
  tasks: ClientTask[];
  /** Worst urgency across the client's tasks. */
  urgency: Urgency;
}

export type ViewerRole = 'ADMIN' | 'MANAGER' | 'USER';

export type TaskScope =
  | { kind: 'all' }
  | { kind: 'location'; locations: string[] }
  | { kind: 'coach'; coachId: string };
